// CardName — a card's name on the Studio desk, or its rename field (MF-1,
// #253; the field is RowRename): the name (two lines, then "…"; the whole
// name on hover), or while renaming the field with the name selected, a note
// under it on a blank Enter and the limit hint near the limit. A save that
// failed leaves one quiet line under the name. Pure: what shows comes from
// the Studio's machine (studioMachine.ts), the outcome goes out.
//
// `data-name` marks the name (and the field in its place) that the canvas's
// press and double-click look for (Studio.tsx); the field owns its keys and pointer (Enter must not open the
// card, a press must not drag it).

export type { CardNaming };
export { CardName };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode, SyntheticEvent } from "react";
import { NAME_MAX } from "../RowOverflow/cleanName.js";
import { RowRename } from "../RowOverflow/RowRename.js";

type CardNameView =
    // `failed`: the name a failed save went back to.
    | { readonly type: "name"; readonly failed: string | undefined }
    | {
          readonly type: "field";
          readonly origin: "made" | "existing";
          readonly blank: boolean;
      };

// What shows, and where the field's outcome goes.
type CardNaming = {
    readonly view: CardNameView;
    readonly onSubmit: (text: string) => void;
    readonly onCancel: () => void;
};

const own = (e: SyntheticEvent) => e.stopPropagation();

const CardName = ({
    title,
    kind,
    className,
    naming: { view, onSubmit, onCancel },
}: {
    readonly title: string;
    readonly kind: "module" | "course" | "other";
    // The name's own look (its card's title class).
    readonly className: string;
    readonly naming: CardNaming;
}): ReactNode => {
    const { t } = useLingui();
    switch (view.type) {
        case "name":
            return (
                <>
                    <p className={className} title={title} data-name="name">
                        {title}
                    </p>
                    {view.failed === undefined ? null : (
                        <p className="bilda-card-name__failed" role="status">
                            {t`Couldn't save the new name. It's back to “${view.failed}”.`}
                        </p>
                    )}
                </>
            );
        case "field":
            return (
                // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the field's keys and pointer from reaching the card (Enter would open it).
                <span
                    className="bilda-card-name__field"
                    data-name="field"
                    onKeyDown={own}
                    onPointerDown={own}
                    onDoubleClick={own}
                >
                    <RowRename
                        initial={title}
                        label={
                            view.origin === "existing"
                                ? t`New name for ${title}`
                                : kind === "course"
                                  ? t`Name the course`
                                  : t`Name the module`
                        }
                        onSubmit={onSubmit}
                        onCancel={onCancel}
                        onBlank={() => onSubmit("")}
                        maxLength={NAME_MAX}
                        blankNote={
                            !view.blank
                                ? undefined
                                : kind === "course"
                                  ? t`Give the course a name.`
                                  : kind === "module"
                                    ? t`Give the module a name.`
                                    : t`Give it a name.`
                        }
                    />
                </span>
            );
        default:
            return exhaustSwitchCase(view);
    }
};
